import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import componentStyles from './pc-video-vimeo.css.lit';
import { OutlineElement } from '@phase2/outline-core';

// Define the Player type for TypeScript
interface VimeoPlayer {
  ready(): Promise<void>;
  destroy(): void;
  on(event: string, callback: (data: any) => void): void;
  off(event: string, callback?: (data: any) => void): void;
  getVideoWidth(): Promise<number>;
  getVideoHeight(): Promise<number>;
  getDuration(): Promise<number>;
  getCurrentTime(): Promise<number>;
  getPaused(): Promise<boolean>;
  getVideoTitle(): Promise<string>;
  getVideoId(): Promise<string>;
  getVideoUrl(): Promise<string>;
}

// Define the Vimeo namespace
declare global {
  interface Window {
    Vimeo?: {
      Player: new (element: HTMLIFrameElement, options?: any) => VimeoPlayer;
    };
    dataLayer?: any[];
  }

  interface HTMLElementTagNameMap {
    'pc-video-vimeo': PcVideoVimeo;
  }
}

// Video engagement event types
export type VideoAction = 'start' | 'pause' | 'resume' | 'complete';

// Interface for the video engagement event data
export interface VideoEngagementData {
  event: string;
  pageURL: string;
  pagePath: string;
  pageTitle: string;
  pageReferrer: string;
  videoName: string;
  videoId: string;
  videoURL: string;
  videoProvider: string;
  videoAction: VideoAction;
  videoPercent: number;
  videoDuration: number;
  videoCurrentTime: number;
  videoVisible: boolean;
}

export interface PcVideoVimeoInterface extends HTMLElement {
  videoID: string;
  hash?: string;
  width?: string;
  debug?: boolean;
}

/**
 * The PC VideoVimeo component
 *
 * @element pc-video-vimeo
 */
@customElement('pc-video-vimeo')
export class PcVideoVimeo
  extends OutlineElement
  implements PcVideoVimeoInterface
{
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, attribute: 'video-id', reflect: true })
  videoID: string;

  @property({ type: String, attribute: 'hash', reflect: true })
  hash?: string;

  @property({ type: String, attribute: 'width', reflect: true })
  width?: string;

  @property({ type: String, attribute: 'name', reflect: true })
  name?: string;

  @property({ type: Boolean, attribute: 'debug', reflect: true })
  debug = false;

  @query('iframe')
  private iframe!: HTMLIFrameElement;

  private player: VimeoPlayer | null = null;
  private vimeoApiLoaded = false;
  private intersectionObserver: IntersectionObserver | null = null;
  private lastVideoAction: VideoAction | null = null;
  private hasSentStart = false;
  private lastPauseTime = 0;
  private lastEndedTime = 0;
  private DEBOUNCE_THRESHOLD = 300; // milliseconds


  @state()
  private isVisible = false;

  @state()
  private videoDuration = 0;

  @state()
  private videoTitle = '';

  @state()
  private videoUrl = '';

  // Track if we've already sent certain events to avoid duplicates
  private milestones: Record<number, boolean> = {
    25: false,
    50: false,
    75: false,
    90: false
  };

  /**
   * Builds the Vimeo player URL with optional hash for private videos
   * @returns The complete Vimeo player URL
   */
  private getPlayerUrl(): string {
    if (this.hash) {
      return `https://player.vimeo.com/video/${this.videoID}?h=${this.hash}`;
    }
    return `https://player.vimeo.com/video/${this.videoID}`;
  }

  /**
   * Initializes the Vimeo player once the component is connected to the DOM
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.loadVimeoApi().then(() => {
      this.initializePlayer();
    });

    // Set up intersection observer to track visibility
    this.setupVisibilityTracking();
  }

  /**
   * Clean up when the component is disconnected
   */
  disconnectedCallback(): void {
    this.destroyPlayer();
    this.destroyVisibilityTracking();
    super.disconnectedCallback();
  }

  /**
   * Loads the Vimeo Player API if not already loaded
   */
  private loadVimeoApi(): Promise<void> {
    return new Promise((resolve) => {
      // If API is already loaded, resolve immediately
      if (window.Vimeo || this.vimeoApiLoaded) {
        this.vimeoApiLoaded = true;
        resolve();
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.getElementById('vimeo-player-api');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          this.vimeoApiLoaded = true;
          resolve();
        });
        return;
      }

      // Create and load the script
      const script = document.createElement('script');
      script.id = 'vimeo-player-api';
      script.src = 'https://player.vimeo.com/api/player.js';
      script.async = true;
      script.onload = () => {
        this.vimeoApiLoaded = true;
        resolve();
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Initializes the Vimeo player and sets up event listeners
   */
  private initializePlayer(): void {
    // Wait for iframe to be available in the DOM
    if (!this.iframe || !window.Vimeo) {
      // Try again after the component is updated
      setTimeout(() => this.initializePlayer(), 100);
      return;
    }

    // Create the player instance
    this.player = new window.Vimeo.Player(this.iframe);

    // When the video is ready, get its dimensions and metadata
    this.player.ready().then(() => {
      this.getVideoDimensions();
      this.getVideoMetadata();

      // Set up event listeners for video engagement tracking
      this.setupVideoEventListeners();
    });
  }

  /**
   * Get video dimensions from the Vimeo player and update host attributes
   */
  private async getVideoDimensions(): Promise<void> {
    if (!this.player) return;

    try {
      const videoWidth = await this.player.getVideoWidth();
      const videoHeight = await this.player.getVideoHeight();

      if (videoWidth && videoHeight) {
        // Set the width attribute on the host element
        this.width = String(videoWidth);

        // Calculate the aspect ratio and set a CSS variable
        const aspectRatio = (videoHeight / videoWidth) * 100;
        this.style.setProperty('--aspect-ratio', `${aspectRatio}%`);

        // Dispatch a custom event with the video dimensions
        this.dispatchEvent(new CustomEvent('video-dimensions', {
          detail: { width: videoWidth, height: videoHeight },
          bubbles: true,
          composed: true
        }));
      }
    } catch (error) {
      console.error('Error getting video dimensions:', error);
    }
  }

  /**
   * Get video metadata from the Vimeo player
   */
  private async getVideoMetadata(): Promise<void> {
    if (!this.player) return;

    const player = this.player;

    try {
      // Get video duration
      player.getDuration()
        .then(duration => {
          this.videoDuration = duration;
        })
        .catch(error => {
          console.error('Error getting video duration:', error);
        });

      // Get video title
      player.getVideoTitle()
        .then(title => {
          this.videoTitle = title;
        })
        .catch(() => {
          // Fall back to title attribute or video ID
          this.videoTitle = this.iframe?.getAttribute('title') || `Vimeo video ${this.videoID}`;
        });

      // Get video URL
      player.getVideoUrl()
        .then(url => {
          this.videoUrl = url;
        })
        .catch(() => {
          // Fall back to iframe src
          this.videoUrl = this.iframe?.src || '';
        });
    } catch (error) {
      console.error('Error getting video metadata:', error);
    }
  }

  /**
   * Set up IntersectionObserver to track video visibility
   */
  private setupVisibilityTracking(): void {
    // Only create the observer if IntersectionObserver is available
    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          // Update visibility state based on intersection
          const isVisible = entries[0]?.isIntersecting || false;

          // If visibility changed, update state
          if (isVisible !== this.isVisible) {
            this.isVisible = isVisible;

            // Dispatch a custom event for visibility change
            this.dispatchEvent(new CustomEvent('video-visibility-changed', {
              detail: { visible: isVisible },
              bubbles: true,
              composed: true
            }));
          }
        },
        {
          root: null, // Use viewport
          threshold: 0.5 // Consider visible when 50% is in view
        }
      );

      // Start observing this element
      this.intersectionObserver.observe(this);
    } else {
      // Fallback for browsers without IntersectionObserver
      this.isVisible = true;
    }
  }

  /**
   * Clean up the IntersectionObserver
   */
  private destroyVisibilityTracking(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  /**
   * Set up event listeners for the Vimeo player
   */
  private setupVideoEventListeners(): void {
    if (!this.player) return;

    const player = this.player; // Store reference to avoid null checks in callbacks

    // Play event
    player.on('play', async () => {
      try {
        const currentTime = await player.getCurrentTime().catch(() => 0);

        // Determine if this is a start or resume event
        if (!this.hasSentStart && currentTime < 1) {
          // First play from beginning (start)
          this.pushVideoEngagementEvent('start');
          this.hasSentStart = true;
          this.lastVideoAction = 'start';
        } else if (this.lastVideoAction === 'pause') {
          // Resume after pause
          this.pushVideoEngagementEvent('resume');
          this.lastVideoAction = 'resume';
        }
      } catch (error) {
        console.error('Error handling play event:', error);
      }
    });

    // Pause event
    player.on('pause', async () => {
      try {
        const now = Date.now();
        if (now - this.lastPauseTime < this.DEBOUNCE_THRESHOLD) {
          if (this.debug) {
            console.log('Debounced pause event');
          }
          return;
        }
        this.lastPauseTime = now;

        // Make sure it's not paused at the end (which would be "complete")
        const currentTime = await player.getCurrentTime().catch(() => 0);
        const duration = await player.getDuration().catch(() => 0);

        // If we're not at the end of the video
        if (currentTime < duration - 0.5) {
          this.pushVideoEngagementEvent('pause');
          this.lastVideoAction = 'pause';
        }
      } catch (error) {
        console.error('Error handling pause event:', error);
      }
    });

    // Ended event
    player.on('ended', () => {
      try {
        const now = Date.now();
        if (now - this.lastEndedTime < this.DEBOUNCE_THRESHOLD) {
          if (this.debug) {
            console.log('Debounced ended event');
          }
          return;
        }

        // Update the timestamp
        this.lastEndedTime = now;
        if (this.lastVideoAction !== 'complete') {

          this.pushVideoEngagementEvent('complete');
          this.lastVideoAction = 'complete';

          // Reset milestones for repeat viewing
          this.milestones = {
            25: false,
            50: false,
            75: false,
            90: false
          };

          // Reset start flag
          this.hasSentStart = false;
        }
      } catch (error) {
        console.error('Error handling ended event:', error);
      }

    });

    // Timeupdate event for progress tracking
    player.on('timeupdate', async (data: any) => {
      try {
        if (!data || typeof data.percent !== 'number') return;

        const percent = Math.floor(data.percent * 100);

        // Check and track milestones
        if (percent >= 25 && !this.milestones[25]) {
          this.milestones[25] = true;
          this.trackMilestone(25);
        }
        if (percent >= 50 && !this.milestones[50]) {
          this.milestones[50] = true;
          this.trackMilestone(50);
        }
        if (percent >= 75 && !this.milestones[75]) {
          this.milestones[75] = true;
          this.trackMilestone(75);
        }
        if (percent >= 90 && !this.milestones[90]) {
          this.milestones[90] = true;
          this.trackMilestone(90);
        }
      } catch (error) {
        console.error('Error handling timeupdate:', error);
      }
    });
  }

  /**
   * Track a video milestone
   */
  private trackMilestone(milestone: number): void {
    // Dispatch a custom event for the milestone
    this.dispatchEvent(new CustomEvent('video-milestone', {
      detail: {
        videoId: this.videoID,
        milestone: milestone,
        videoTitle: this.videoTitle
      },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Push video engagement event to the data layer
   */
  private async pushVideoEngagementEvent(action: VideoAction): Promise<void> {
    if (!this.player) return;

    try {
      // Get current video state
      let currentTime = 0;
      let duration = this.videoDuration;

      try {
        currentTime = await this.player.getCurrentTime();
        if (!duration && this.player) {
          duration = await this.player.getDuration();
        }
      } catch (error) {
        console.error('Error getting video timing:', error);
      }

      const percent = duration > 0 ? Math.floor((currentTime / duration) * 100) : 0;

      // Build the engagement data
      const engagementData: VideoEngagementData = {
        event: 'custom.videoEngagement',
        pageURL: document.location.href,
        pagePath: document.location.pathname,
        pageTitle: document.title,
        pageReferrer: document.referrer,
        videoName: this.name || this.videoTitle || `Vimeo video ${this.videoID}`,
        videoId: this.videoID,
        videoURL: this.videoUrl || this.iframe.src,
        videoProvider: 'Vimeo',
        videoAction: action,
        videoPercent: percent,
        videoDuration: Math.round(duration),
        videoCurrentTime: Math.round(currentTime),
        videoVisible: this.isVisible
      };

      // Push to data layer
      if (window.dataLayer) {
        window.dataLayer.push(engagementData);
      }

      // Dispatch a custom event with the same data
      // This will bubble up through shadow DOM boundaries
      this.dispatchEvent(new CustomEvent('video-engagement', {
        detail: engagementData,
        bubbles: true,
        composed: true
      }));

      // Log event in console (can be controlled via debug parameter)
      if (this.hasAttribute('debug') || localStorage.getItem('video_debug') === 'true') {
        console.log('Video engagement event:', action, engagementData);
      }
    } catch (error) {
      console.error('Error pushing video engagement event:', error);
    }
  }

  /**
   * Destroy the player instance to clean up resources
   */
  private destroyPlayer(): void {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  }

  /**
   * Update component when attributes change
   */
  updated(changedProperties: Map<string, any>): void {
    super.updated(changedProperties);

    // Reinitialize player if videoID or hash changes
    if (changedProperties.has('videoID') || changedProperties.has('hash')) {
      this.destroyPlayer();
      // Reset tracking state
      this.hasSentStart = false;
      this.lastVideoAction = null;
      this.milestones = {
        25: false,
        50: false,
        75: false,
        90: false
      };

      // Wait for the iframe to be updated with the new URL
      setTimeout(() => this.initializePlayer(), 100);
    }
  }

  render(): TemplateResult {
    return html`
      <iframe
        src="${this.getPlayerUrl()}"
        title="Vimeo video ${this.videoID}"
        frameborder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowfullscreen
      ></iframe>
    `;
  }
}
